import { useCallback, useEffect, useMemo, useState } from 'react'

import { requestWeeklyDigest } from '../_data/renter-llm'
import {
  WeeklyDigestSummary,
  computeWeeklyDigestSummary,
} from '../_data/weekly-digest-summary'
import {
  CachedWeeklyDigest,
  UseWeeklyDigestValue,
} from '../_types/weekly-digest'
import { useAchievements } from './use-achievements'
import { useAuth } from './use-auth'
import { useImportedListings } from './use-imported-listings'
import { useListingDecisions } from './use-listing-decisions'
import { useQuickPassProfile } from './use-quickpass-profile'

const WEEK_BUCKET_MS = 7 * 24 * 60 * 60 * 1000
const CACHE_PREFIX = 'quickpass.weeklyDigest:v1:'

const computeBucketKey = (now: number): string => {
  const bucket = Math.floor(now / WEEK_BUCKET_MS)
  return `bucket-${bucket}`
}

const readCache = (
  userId: string,
  bucketKey: string,
): CachedWeeklyDigest | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${userId}`)
    if (raw === null) {
      return null
    }
    const parsed = JSON.parse(raw) as CachedWeeklyDigest
    if (parsed.bucketKey !== bucketKey) {
      return null
    }
    return parsed
  } catch (error) {
    console.error('[weekly-digest] cache read failed', error)
    return null
  }
}

const writeCache = (userId: string, payload: CachedWeeklyDigest): void => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(payload))
  } catch (error) {
    console.error('[weekly-digest] cache write failed', error)
  }
}

/**
 * Computes a weekly activity summary, requests an LLM digest from the
 * backend at most once per ISO-week-bucket per user, and caches the
 * result to localStorage so the activity page is snappy on revisit.
 *
 * @returns {UseWeeklyDigestValue} the digest state + refresh trigger
 */
const useWeeklyDigest = (): UseWeeklyDigestValue => {
  const { quickPassUser } = useAuth()
  const { profile } = useQuickPassProfile()
  const { listings } = useImportedListings()
  const { tags, notes } = useListingDecisions()
  const { unlockedIds } = useAchievements()

  const [state, setState] = useState<UseWeeklyDigestValue['state']>('idle')
  const [resultPayload, setResultPayload] =
    useState<UseWeeklyDigestValue['result']>(null)
  const [generatedAt, setGeneratedAt] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [forceRefreshToken, setForceRefreshToken] = useState<number>(0)

  const summary = useMemo<WeeklyDigestSummary | null>(() => {
    if (profile === null) {
      return null
    }
    return computeWeeklyDigestSummary(
      profile,
      listings,
      tags,
      notes,
      unlockedIds,
      Date.now(),
    )
  }, [profile, listings, tags, notes, unlockedIds])

  const userId = quickPassUser?.id ?? ''

  useEffect(() => {
    if (userId === '' || summary === null) {
      return
    }

    const bucketKey = computeBucketKey(Date.now())
    const cached = forceRefreshToken === 0 ? readCache(userId, bucketKey) : null

    if (cached !== null) {
      setResultPayload(cached.result)
      setGeneratedAt(cached.generatedAt)
      setState(cached.result === null ? 'empty' : 'ready')
      return
    }

    if (summary.hasMeaningfulActivity === false) {
      const emptyEntry: CachedWeeklyDigest = {
        bucketKey,
        summary,
        result: null,
        generatedAt: Date.now(),
      }
      writeCache(userId, emptyEntry)
      setResultPayload(null)
      setGeneratedAt(emptyEntry.generatedAt)
      setState('empty')
      return
    }

    let isMounted = true
    setState('loading')
    setErrorMessage('')

    const run = async (): Promise<void> => {
      try {
        const llmResult = await requestWeeklyDigest({ summary })

        if (isMounted === false) {
          return
        }

        if (llmResult === null) {
          setState('offline')
          return
        }

        const fresh: CachedWeeklyDigest = {
          bucketKey,
          summary,
          result: llmResult,
          generatedAt: Date.now(),
        }
        writeCache(userId, fresh)
        setResultPayload(llmResult)
        setGeneratedAt(fresh.generatedAt)
        setState('ready')
      } catch (error) {
        if (isMounted === false) {
          return
        }
        setState('error')
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Could not pull this week's digest.",
        )
      }
    }

    void run()

    return () => {
      isMounted = false
    }
  }, [userId, summary, forceRefreshToken])

  const refresh = useCallback(async (): Promise<void> => {
    if (userId !== '') {
      try {
        localStorage.removeItem(`${CACHE_PREFIX}${userId}`)
      } catch (error) {
        console.error('[weekly-digest] cache clear failed', error)
      }
    }
    setForceRefreshToken(token => token + 1)
  }, [userId])

  return {
    state,
    summary,
    result: resultPayload,
    generatedAt,
    errorMessage,
    refresh,
  }
}

export { useWeeklyDigest }
